/**
 * The contract between Wallet's web app and the Points Capture extension.
 *
 * ONE file, imported by both halves (`extension/src/externalPort.ts` and
 * `web/src/lib/captureBridge.ts`), because every field here is a fact stored
 * twice the moment it is written down twice. The repo builds both halves, so a
 * shared module costs nothing and a duplicated one drifts silently: a verb the
 * page sends and the worker does not know is a button that does nothing, with
 * no error either side can report.
 *
 * It carries NO chrome and NO DOM types, so it compiles under both tsconfigs.
 *
 * Why an external port at all, rather than a content script posting into the
 * page: a content script on wallet.mattfinkel.com would put the extension's
 * message channel inside the page's own JavaScript context, where anything
 * running on the page can reach it, and it would need a host permission for
 * Wallet. `externally_connectable` gives the page a channel Chrome itself
 * authenticates by origin, and leaves the page's DOM alone.
 */

/**
 * The wire version. Bumped when a field's MEANING changes, never for an
 * addition the other half can ignore.
 *
 * Both halves refuse a message that is not exactly this, because a run reads
 * financial sites in the operator's logged-in tabs: a half-understood start is
 * worse than a refused one. The refusal is what lets Wallet say "rebuild the
 * extension" instead of "not installed", so it is answered rather than dropped.
 */
export const PROTOCOL_VERSION = 1;

/** The port's name. Both halves name it; a mismatch is a silent hang. */
export const PORT_NAME = "wallet-capture";

/**
 * The extension's id, which is stable because the manifest carries a `key`.
 *
 * An unpacked extension's id is otherwise derived from its directory path, so
 * it changes between machines and checkouts and no web page can know it in
 * advance. `extension/src/capturePort.test.ts` derives this from the manifest's
 * key the way Chrome does and fails if the two disagree -- the one guard that
 * catches a regenerated key, whose only other symptom is Chrome's
 * "Receiving end does not exist", which reads exactly like "not installed".
 */
export const EXTENSION_ID = "opamhbfpibbmhfdklgokgmpfnjplfeef";

/**
 * Exactly which origins may drive a run.
 *
 * The manifest's `externally_connectable.matches` cannot express a port (Chrome
 * matches scheme and host only), so it necessarily admits every port on
 * loopback. This list is the real boundary and it is checked in code, on the
 * origin Chrome itself reports for the sender. Exact string equality: a prefix
 * or `includes` test takes `https://wallet.mattfinkel.com.evil.test`.
 *
 * BOTH loopback ports, because this repo serves on two: `npm run dev` is 5173
 * (vite.config.ts) and `npm run preview` is 4173. Listing only one is a
 * handshake that silently fails in ordinary development and reads exactly like
 * the extension not being loaded -- which is what the first local test would
 * have hit. Both spellings of loopback, since Chrome reports the origin as the
 * page's own and `localhost` and `127.0.0.1` are different origins.
 */
export const ALLOWED_ORIGINS = [
  "https://wallet.mattfinkel.com",
  "http://127.0.0.1:5173",
  "http://localhost:5173",
  "http://127.0.0.1:4173",
  "http://localhost:4173",
] as const;

export function isAllowedOrigin(origin: unknown): boolean {
  return typeof origin === "string"
    && (ALLOWED_ORIGINS as readonly string[]).includes(origin);
}

/**
 * Everything the page may ask for, and nothing more.
 *
 * Deliberately absent, each reachable from the extension's own pages and from
 * nowhere else: `signIn` (opens a Google window), `probe` (reads the active
 * tab's DOM and returns the value it found), `rejectRun` (writes to the
 * database), `apiBase` (decides where a Firebase ID token is sent), and
 * anything that would return a balance, an offer's merchant or raw issuer text.
 * The page gets catalog names, counts, codes and run state, and may reload the
 * extension so a new build on disk takes effect; the extension refuses that
 * while a run is live.
 */
export const VERBS = ["hello", "catalog", "snapshot", "start", "stop", "reload"] as const;

export type Verb = (typeof VERBS)[number];

interface Envelope {
  v: number;
  id: string;
}

export type Request =
  | (Envelope & { verb: "hello" })
  | (Envelope & { verb: "catalog" })
  | (Envelope & { verb: "snapshot" })
  | (Envelope & { verb: "start"; slugs?: string[] })
  | (Envelope & { verb: "stop" })
  | (Envelope & { verb: "reload" });

export type RejectCode =
  | "not_an_object"
  | "bad_envelope"
  | "protocol_mismatch"
  | "unknown_verb"
  | "unknown_field"
  | "bad_slugs";

/** Codes the handler adds once a request has parsed. */
export type FailureCode = RejectCode | "busy" | "failed";

export type ParseResult =
  | { ok: true; request: Request }
  | { ok: false; code: RejectCode };

/** Base keys every request carries, plus the keys each verb adds. */
const EXTRA_KEYS: Record<Verb, readonly string[]> = {
  hello: [],
  catalog: [],
  snapshot: [],
  start: ["slugs"],
  stop: [],
  reload: [],
};

/**
 * How many programs one start may name.
 *
 * Generous against the real registry (21 programs and 5 offers sources) and
 * bounded because the list is held in memory for the length of a run and echoed
 * into the run's log.
 */
export const MAX_SLUGS = 200;

/**
 * Whether a slug list is one this protocol will carry.
 *
 * An EMPTY list is refused rather than treated as "all": the background filters
 * the canonical lists by membership, so an empty filter runs nothing while
 * reporting a started run, which is indistinguishable from a hung one. Run all
 * is the ABSENCE of the field, which is what `startRun(undefined)` already
 * means on the other side.
 *
 * A comma inside an entry is refused because that is how the POPUP encodes a
 * list in one `data-run` attribute. Over this port a list is a list; a comma
 * means the web app built a popup-shaped string, which would match no program
 * and run nothing.
 */
function slugsOk(value: unknown): value is string[] {
  return Array.isArray(value)
    && value.length > 0
    && value.length <= MAX_SLUGS
    && value.every((s) => typeof s === "string" && s.length > 0 && !s.includes(","));
}

/**
 * Validate a message off the wire into a `Request`, or say why not.
 *
 * Closed, not merely typed: an undeclared field is a refusal rather than
 * something ignored. A field silently dropped is how a misspelling becomes a
 * run that did the wrong thing and reported success.
 *
 * The order of the checks is load-bearing, because the caller can only reply to
 * a message it can address: the id is checked before the version, so a message
 * with no id is unanswerable (and dropped) while a version mismatch always gets
 * an answer the other side can act on.
 */
export function parseRequest(message: unknown): ParseResult {
  if (typeof message !== "object" || message === null || Array.isArray(message)) {
    return { ok: false, code: "not_an_object" };
  }
  const m = message as Record<string, unknown>;
  if (typeof m.id !== "string" || m.id.length === 0) return { ok: false, code: "bad_envelope" };
  if (m.v !== PROTOCOL_VERSION) return { ok: false, code: "protocol_mismatch" };
  if (typeof m.verb !== "string" || !(VERBS as readonly string[]).includes(m.verb)) {
    return { ok: false, code: "unknown_verb" };
  }
  const verb = m.verb as Verb;
  const allowed = new Set<string>(["v", "id", "verb", ...EXTRA_KEYS[verb]]);
  if (Object.keys(m).some((key) => !allowed.has(key))) {
    return { ok: false, code: "unknown_field" };
  }
  if (verb === "start") {
    if ("slugs" in m && !slugsOk(m.slugs)) return { ok: false, code: "bad_slugs" };
    const slugs = m.slugs as string[] | undefined;
    return { ok: true, request: slugs === undefined
      ? { v: PROTOCOL_VERSION, id: m.id, verb }
      : { v: PROTOCOL_VERSION, id: m.id, verb, slugs } };
  }
  return { ok: true, request: { v: PROTOCOL_VERSION, id: m.id, verb } };
}

/**
 * The id a reply should carry, or null when the message cannot be answered.
 *
 * Separate from `parseRequest`, which reports WHY a message was refused and
 * deliberately does not carry an id into its failure: the two answer different
 * questions, and a failure shape that sometimes carries an id is a shape every
 * caller has to branch on.
 */
export function replyId(message: unknown): string | null {
  if (typeof message !== "object" || message === null) return null;
  const id = (message as Record<string, unknown>).id;
  return typeof id === "string" && id.length > 0 ? id : null;
}

/* ---------------------------------------------------------------- replies */

export interface HelloResult {
  /** This half's protocol version, so an older page can say what it needs. */
  protocol: number;
  /** The manifest version, which is the only build marker chrome://extensions shows. */
  extension: string;
  /** The build's commit, the popup's answer to "is this the code I just built". */
  commit: string;
  /** When this build was made. Absent from builds before 1.2.0. */
  built?: string;
  /**
   * The build in the extension's directory, which a reload would load, or null
   * when it could not be read. Absent from builds before 1.2.0.
   */
  on_disk?: { version: string; commit: string; built: string } | null;
  /** This build answers `reload`. Absent from builds before 1.2.0. */
  reload?: true;
}

export interface CatalogProgram {
  slug: string;
  name: string;
  /** Wallet's newest counted reading, YYYY-MM-DD, or null for never. */
  last_as_of: string | null;
  /**
   * The card issuer whose currency this is, when the registry names one.
   *
   * GROUPING only, and the one fact that lets Wallet put an issuer currency
   * beside that issuer's offers page. Deliberately not a pairing: which programs
   * a Run reads alongside an offers page is `points_slugs` on the offers entry,
   * and a program can be grouped under an issuer whose offers page is not served
   * at all -- which is the case that made the field necessary.
   */
  issuer?: string;
  /**
   * How often this program is worth re-reading, as the registry states it.
   *
   * What "overdue" means. Without it the page had one threshold for every
   * program, so a quarterly program read as overdue every fortnight.
   */
  cadence?: string | null;
}

export interface CatalogOfferSource {
  slug: string;
  issuer: string;
  /**
   * The programs one Run reads alongside this source, as the REGISTRY pairs
   * them. Never derived from hosts on either side: Chase's points and offers
   * are on different hosts and are paired, Amex's share one and the pairing is
   * still the registry's to state.
   */
  points_slugs?: string[];
  /**
   * The day this source's page was last READ, YYYY-MM-DD, or null for never.
   *
   * Not derived from its offers: `offers.last_seen_at` is stamped on offers the
   * capture stored, and "nothing was worth adding" is the ordinary month, so a
   * source read weekly would read as never. The server records the read itself.
   */
  last_read_on?: string | null;
}

export interface Catalog {
  /** The hash Wallet serves with the rules, or null when it could not be asked. */
  rules_version: string | null;
  programs: CatalogProgram[];
  offer_sources: CatalogOfferSource[];
}

/**
 * The wire shapes of a run's state.
 *
 * These mirror `extension/src/run.ts`, which remains the authority: the
 * extension asserts its own types are assignable to these
 * (`extension/src/protocolShapes.test.ts`), so adding a field there cannot
 * silently change what the page is promised, and narrowing one fails typecheck.
 *
 * Everything here is a name, a count, a date, a status word or a code. No
 * balance, no amount, no merchant, no member number and no raw issuer text may
 * appear in any field, which is the same rule the extension's own log keeps.
 */
export interface WireLogLine {
  /** Milliseconds since the run started. */
  at: number;
  text: string;
}

export interface WireProgress {
  run_id: string;
  total: number;
  done: {
    slug: string;
    name: string;
    read: number;
    of: number;
    /**
     * Why this one is not a plain count. `stopped` is the operator's own doing
     * and reads as neither a failure nor a success. (2026-10-04)
     */
    note: "signed_out" | "error" | "stopped" | null;
    counts?: "readings" | "cards";
  }[];
  current: {
    slug: string;
    name: string;
    since: number;
    waiting: boolean;
    /** The only thing a run waits on the operator for. */
    waiting_for?: "signin";
  }[];
  stopped: boolean;
}

export interface WireProgramResult {
  slug: string;
  name: string;
  /**
   * `stopped` is distinct from `error` on purpose: Stop closes the run's tabs, so
   * an interrupted program throws exactly what a crashed tab throws, and calling
   * that "could not run" blames the site for something the operator did.
   */
  state: "recorded" | "partial" | "failed" | "signed_out" | "error" | "stopped";
  /**
   * `"balance:recorded"`, `"credit:held_implausible"`: what Wallet did with one
   * reading, and which KIND of reading it was. Read with `tallyStatuses`.
   */
  statuses: string[];
  failures: { rule_key: string; code: string }[];
  error?: string;
}

/** Which kind of reading a `statuses` entry describes. */
export type WireReadingKind = "balance" | "credit";

export interface StatusTally {
  /** Point balances Wallet took: one per program per run. */
  balances: number;
  /** Credit readings Wallet took: a program may send any number. */
  credits: number;
  /** Entries from a run recorded before 1.0.2, which do not name their kind. */
  unkinded: number;
  held: number;
  refused: number;
}

/**
 * The ONE reader of a program's `statuses`, shared by the popup's run line and
 * Wallet's Refresh page. Balances and credits are counted apart because one slug
 * carries one balance and up to nine credits.
 */
export function tallyStatuses(statuses: string[]): StatusTally {
  const tally: StatusTally = { balances: 0, credits: 0, unkinded: 0, held: 0, refused: 0 };
  for (const entry of statuses) {
    const at = entry.indexOf(":");
    const kind = at === -1 ? null : entry.slice(0, at);
    const status = at === -1 ? entry : entry.slice(at + 1);
    if (status.startsWith("held")) tally.held += 1;
    else if (status !== "recorded") tally.refused += 1;
    else if (kind === "balance") tally.balances += 1;
    else if (kind === "credit") tally.credits += 1;
    else tally.unkinded += 1;
  }
  return tally;
}

export interface WireCardOfferResult {
  card: string;
  state: "recorded" | "failed";
  /** Offers read over every route, eligible and enrolled. */
  read: number;
  skipped: number;
  posted?: number;
  complete?: boolean;
  counts?: Record<string, number>;
  reason?: string;
  failures: string[];
  /**
   * The per-offer text step: how many offers Wallet asked to have opened, how
   * many were read and passed over, and why it stopped early if it did.
   */
  details?: { wanted: number; read: number; skipped: number; stopped?: string };
  /** Adds this card's POST settled: confirmed taken, or read still not added. */
  adds?: { confirmed: number; unconfirmed: number };
}

export interface WireOfferResult {
  slug: string;
  issuer: string;
  cards: WireCardOfferResult[];
  /**
   * The add step, for a source that adds for you: why nothing was planned
   * (`held`), or what was planned, pressed, passed over and confirmed, and why
   * it stopped early. Counts and codes only.
   */
  adds?: {
    held?: string; planned: number; pressed: number; skipped: number;
    confirmed: number; unconfirmed: number; stopped?: string;
  };
  /** The person was asked to sign in and had not by the time the run gave up. */
  signed_out: boolean;
  /** The run was stopped while this source was reading. See WireProgramResult.state. */
  stopped?: boolean;
  error?: string;
}

export interface WireRunSummary {
  run_id: string;
  finished_at: string;
  programs: WireProgramResult[];
  post_failed: boolean;
  stopped: boolean;
  error?: string;
  offers?: WireOfferResult[];
}

export interface Snapshot {
  /** Whether a run is going, judged by the extension's own `runIsLive`. */
  live: boolean;
  /** Stop has been asked for and the run is winding up. */
  stopping: boolean;
  progress: WireProgress | null;
  log: WireLogLine[];
  last_run: WireRunSummary | null;
  /**
   * A run that started and stopped beating without finishing: the worker was
   * shut down mid-run. Distinct from a failed run, which has a summary.
   */
  interrupted: boolean;
}

export interface StartResult { started: true }
export interface StopResult { stopping: true }
/** Answered before the reload, which drops this port; the page reconnects. */
export interface ReloadResult { reloading: true }

export type Result = HelloResult | Catalog | Snapshot | StartResult | StopResult | ReloadResult;

export type Reply =
  | { v: number; id: string; ok: true; result: Result }
  | { v: number; id: string; ok: false; code: FailureCode };

export type Event = { v: number; event: "snapshot"; snapshot: Snapshot };

export const reply = (id: string, result: Result): Reply =>
  ({ v: PROTOCOL_VERSION, id, ok: true, result });

export const fail = (id: string, code: FailureCode): Reply =>
  ({ v: PROTOCOL_VERSION, id, ok: false, code });

export const snapshotEvent = (snapshot: Snapshot): Event =>
  ({ v: PROTOCOL_VERSION, event: "snapshot", snapshot });

/** Whether a message off the port is a reply to a request we sent. */
export function isReply(message: unknown): message is Reply {
  if (typeof message !== "object" || message === null) return false;
  const m = message as Record<string, unknown>;
  return typeof m.id === "string" && typeof m.ok === "boolean";
}

/** Whether a message off the port is an unsolicited snapshot push. */
export function isSnapshotEvent(message: unknown): message is Event {
  if (typeof message !== "object" || message === null) return false;
  const m = message as Record<string, unknown>;
  return m.event === "snapshot" && typeof m.snapshot === "object" && m.snapshot !== null;
}
